import { API_PREFIX, HEADERS } from '@sunucupark/shared/lite';
import type { ApiError as ApiErrorBody } from '@sunucupark/shared';
import type { z } from 'zod';
import { AppError, ClientErrorCode } from '../../domain/errors';
import { attempt, type Result } from '../../domain/result';
import type { NetworkStatus } from '../../application/ports/network-status';
import type { SessionPort } from '../../application/ports/session';
import { buildQuery, type Query } from './query';
import type {
  HttpMethod,
  HttpResponse,
  HttpTransport,
  MultipartBody,
  UploadProgress,
} from './transport';

/** Requests answered by the mail server; only these prove it is reachable again. */
const MAIL_SERVER_PATHS = /^\/(mail|calendar|contacts|tasks|settings)\//;
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * The contract an answer must match: the schema itself, or a function that
 * loads it. The browser passes a loader so zod and the contracts stay out of
 * the first download and arrive while the request is in flight (ADR 0006).
 */
export type SchemaSource<T> = z.ZodType<T> | (() => Promise<z.ZodType<T>>);

export interface ApiRequest<T> {
  method?: HttpMethod;
  /** Path from the shared route table, without the API prefix. */
  path: string;
  query?: Query;
  /** JSON payload; ignored when `multipart` is set. */
  body?: unknown;
  multipart?: MultipartBody;
  /**
   * Told how much of the body has gone out, for a progress bar. Ignored when
   * the platform's transport cannot report it; the request still runs.
   */
  onUploadProgress?: (progress: UploadProgress) => void;
  /** Contract the answer must match; the result is the parsed value. */
  schema?: SchemaSource<T>;
  timeoutMs?: number;
  /** `none` for sign-in and the other public routes. */
  auth?: 'required' | 'none';
  /** Routes that authenticate with the refresh cookie instead (`/auth/web/*`). */
  credentials?: 'include' | 'omit' | 'same-origin';
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiClientOptions {
  /** Gateway origin, e.g. `https://api.postamerkezi.com`; a trailing slash is ignored. */
  baseUrl: string;
  transport: HttpTransport;
  /** Client identification sent with every request. */
  client: { version: string; platform: string };
  locale: () => string;
  session?: SessionPort;
  network?: NetworkStatus;
  apiPrefix?: string;
  installationId?: () => string | undefined;
  requestId?: () => string;
  /** Headers added to every request, e.g. the CSRF marker of the browser client. */
  defaultHeaders?: () => Record<string, string>;
  /** Told when an answer proves the mail server is down or reachable again. */
  onMailServerStatus?: (status: 'up' | 'down') => void;
  timeoutMs?: number;
}

/** Error codes that mean the mail server itself is unreachable. */
const MAIL_SERVER_DOWN_CODES: ReadonlySet<string> = new Set([
  'MAIL_SERVER_UNAVAILABLE',
  'MAIL_SERVER_AUTH_EXPIRED',
]);

/**
 * The gateway client: JSON in and out, contracts from `@sunucupark/shared`,
 * the error envelope turned into {@link AppError}, timeouts, and one
 * transparent refresh-and-retry. It knows nothing about how the session is
 * carried — that is the {@link SessionPort}'s job.
 */
export class ApiClient {
  private readonly prefix: string;

  constructor(private readonly options: ApiClientOptions) {
    this.prefix = options.apiPrefix ?? API_PREFIX;
  }

  /** Absolute URL of a path, e.g. for a download the platform performs itself. */
  url(path: string, query?: Query): string {
    return `${this.options.baseUrl.replace(/\/$/, '')}${this.prefix}${path}${buildQuery(query)}`;
  }

  /** Runs the request, throwing an {@link AppError} on any failure. */
  async request<T = unknown>(request: ApiRequest<T>): Promise<T> {
    if (this.options.network && !this.options.network.isOnline()) {
      throw new AppError(ClientErrorCode.OFFLINE, 'The device is offline');
    }
    try {
      return await this.execute(request);
    } catch (error) {
      const appError = AppError.from(error);
      const session = this.options.session;
      if (session && request.auth !== 'none' && appError.shouldRefreshAndRetry) {
        await session.refresh();
        return this.execute(request);
      }
      if (session && appError.terminatesSession) session.onTerminated(appError);
      throw appError;
    }
  }

  /** The same request as a {@link Result}, for use cases that handle failures as values. */
  requestResult<T = unknown>(request: ApiRequest<T>): Promise<Result<T, AppError>> {
    return attempt(() => this.request(request));
  }

  private async execute<T>(request: ApiRequest<T>): Promise<T> {
    if (request.signal?.aborted)
      throw new AppError(ClientErrorCode.ABORTED, `Request to ${request.path} was cancelled`);
    const controller = new AbortController();
    const timeoutMs = request.timeoutMs ?? this.options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const abortFromCaller = () => controller.abort();
    request.signal?.addEventListener('abort', abortFromCaller, { once: true });

    const headers = await this.buildHeaders(request);
    const transport = this.options.transport;
    const wire = {
      url: this.url(request.path, request.query),
      method: request.method ?? 'GET',
      headers,
      body:
        request.multipart ?? (request.body === undefined ? undefined : JSON.stringify(request.body)),
      signal: controller.signal,
      credentials: request.credentials ?? 'omit',
    } as const;
    let response: HttpResponse;
    try {
      // Progress is a nicety: a transport that cannot report it still sends the request.
      response =
        request.onUploadProgress && transport.sendWithProgress
          ? await transport.sendWithProgress(wire, request.onUploadProgress)
          : await transport.send(wire);
    } catch (error) {
      if (timedOut)
        throw new AppError(ClientErrorCode.TIMEOUT, `Request to ${request.path} timed out`, {
          cause: error,
        });
      throw AppError.from(error);
    } finally {
      clearTimeout(timer);
      request.signal?.removeEventListener('abort', abortFromCaller);
    }

    const text = await response.text();
    let json: unknown;
    if (text.length > 0) {
      try {
        json = JSON.parse(text);
      } catch (error) {
        if (response.ok) {
          throw new AppError(
            ClientErrorCode.PARSE,
            `The gateway answered ${request.path} with invalid JSON`,
            {
              status: response.status,
              cause: error,
            },
          );
        }
      }
    }

    if (!response.ok) throw this.errorOf(json, response.status);
    if (request.auth !== 'none' && MAIL_SERVER_PATHS.test(request.path))
      this.options.onMailServerStatus?.('up');
    if (!request.schema) return json as T;
    const schema = typeof request.schema === 'function' ? await request.schema() : request.schema;
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      throw new AppError(
        ClientErrorCode.PARSE,
        `The answer of ${request.path} did not match the contract`,
        {
          status: response.status,
          details: parsed.error.issues,
        },
      );
    }
    return parsed.data;
  }

  private errorOf(json: unknown, status: number): AppError {
    const envelope = json as Partial<ApiErrorBody> | undefined;
    const code = envelope?.error?.code;
    if (code) {
      if (MAIL_SERVER_DOWN_CODES.has(code)) this.options.onMailServerStatus?.('down');
      return AppError.fromBody(envelope as ApiErrorBody, status);
    }
    return new AppError(status >= 500 ? 'INTERNAL_ERROR' : 'VALIDATION_ERROR', `HTTP ${status}`, {
      status,
    });
  }

  private async buildHeaders(request: ApiRequest<unknown>): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      [HEADERS.appVersion]: this.options.client.version,
      [HEADERS.appPlatform]: this.options.client.platform,
      [HEADERS.acceptLanguage]: this.options.locale(),
      ...this.options.defaultHeaders?.(),
      ...request.headers,
    };
    if (this.options.requestId) headers[HEADERS.requestId] = this.options.requestId();
    const installationId = this.options.installationId?.();
    if (installationId) headers[HEADERS.installationId] = installationId;
    if (request.multipart === undefined && request.body !== undefined)
      headers['Content-Type'] = 'application/json';
    if (request.auth !== 'none' && this.options.session) {
      Object.assign(headers, await this.options.session.authHeaders());
    }
    return headers;
  }
}
